"use client"

import { ampli } from "@/ampli"
import { SubscriptionCheckoutModal } from "@/components/Subscriptions/SubscriptionCheckoutModal"
import { graphql } from "@/gql"
import {
  AuthenticatedJobPostingClientViewerDocument,
  AuthenticatedJobPostingClientViewerQuery,
  AuthenticatedJobPostingCreativeViewerDocument,
  AuthenticatedJobPostingCreativeViewerQuery,
} from "@/gql/graphql"
import type { NovaSession } from "@/types"
import { decodeGraphqlNodeId } from "@/tracking"
import { useQuery } from "@apollo/client"
import { FC, useCallback, useEffect, useState } from "react"
import { ApplyToJobDrawer } from "../../components/ApplyToJob/ApplyToJobDrawer"
import { JobApplicationDrawer } from "../../components/JobApplication/JobApplicationDrawer"
import { AuthenticatedJobDetailsFooter } from "../../components/JobDetails/JobDetailsAuthenticated/AuthenticatedJobDetailsFooter"
import { AuthenticatedJobDetailsContent } from "../../components/JobDetails/JobDetailsAuthenticated/AuthenticatedJobsDetailsContent"
import { JobsSideDrawerFooterTemplate } from "../../components/JobsSideDrawerTemplate"

graphql(`
  query AuthenticatedJobPostingCreativeViewer($jobPostingId: ID!, $creativeId: ID!) {
    jobPosting(id: $jobPostingId) {
      id
      __typename
      ... on CreativeJobPosting {
        position {
          id
        }
      }
      viewerRedactionReason
      ...AuthenticatedJobDetailsContentJobPosting
      ...AuthenticatedJobDetailsFooterCreativeViewer
    }
    individualCreative(id: $creativeId) {
      id
      __typename
    }
  }
`)

graphql(`
  query AuthenticatedJobPostingClientViewer($jobPostingId: ID!) {
    jobPosting(id: $jobPostingId) {
      id
      __typename
      ... on ClientJobPosting {
        position {
          id
        }
      }
      ...AuthenticatedJobDetailsContentJobPosting
      ...AuthenticatedJobDetailsFooterClientViewer
    }
  }
`)

interface Props {
  jobPostingId: string
  clientViewerServerData?: AuthenticatedJobPostingClientViewerQuery | null
  creativeViewerServerData?: AuthenticatedJobPostingCreativeViewerQuery | null
  serverSession: NovaSession
}

export const JobAuthenticatedClientPage: FC<Props> = ({
  serverSession,
  jobPostingId,
  creativeViewerServerData,
  clientViewerServerData,
}) => {
  const [openCheckoutModal, setOpenCheckoutModal] = useState(false)
  const [applyModal, setApplyModal] = useState<{
    open: boolean
    jobPostingId: string | null
    positionId: string | null
  }>({
    open: false,
    jobPostingId: null,
    positionId: null,
  })
  const [applicationModal, setApplicationModal] = useState<{
    open: boolean
    jobPostingId: string | null
    positionId: string | null
  }>({
    open: false,
    jobPostingId: null,
    positionId: null,
  })

  const viewerIsCreative = serverSession.account === "creative"

  const { data: creativeViewerClientData } = useQuery(AuthenticatedJobPostingCreativeViewerDocument, {
    fetchPolicy: "cache-and-network",
    variables: { jobPostingId, creativeId: serverSession?.user?.creative?.id! },
    context: {
      version: 2,
    },
    skip: !viewerIsCreative,
  })

  const { data: clientViewerClientData } = useQuery(AuthenticatedJobPostingClientViewerDocument, {
    fetchPolicy: "cache-and-network",
    variables: { jobPostingId },
    context: {
      version: 2,
    },
    skip: viewerIsCreative,
  })

  const serverData = viewerIsCreative ? creativeViewerServerData : clientViewerServerData
  const clientData = viewerIsCreative ? creativeViewerClientData : clientViewerClientData

  const creativeViewerData = creativeViewerClientData || creativeViewerServerData
  const clientViewerData = clientViewerClientData || clientViewerServerData

  const data = clientData || serverData

  const sharedJobPosting =
    data?.jobPosting?.__typename === "CreativeJobPosting" || data?.jobPosting?.__typename === "ClientJobPosting"
      ? data?.jobPosting
      : null

  const viewJobPosting = useCallback(() => {
    fetch("/api/view/job", {
      method: "POST",
      body: JSON.stringify({ id: sharedJobPosting?.id }),
    })
  }, [sharedJobPosting?.id])

  useEffect(() => {
    viewJobPosting()
  }, [viewJobPosting, sharedJobPosting?.id])

  const clientViewerJobPosting =
    clientViewerData?.jobPosting?.__typename === "CreativeJobPosting" ||
    clientViewerData?.jobPosting?.__typename === "ClientJobPosting"
      ? clientViewerData.jobPosting
      : null

  const creativeViewerJobPosting =
    creativeViewerData?.jobPosting?.__typename === "CreativeJobPosting" ||
    creativeViewerData?.jobPosting?.__typename === "ClientJobPosting"
      ? creativeViewerData.jobPosting
      : null

  const creative =
    clientViewerData?.jobPosting?.__typename === "CreativeJobPosting"
      ? creativeViewerClientData?.individualCreative
      : null

  const onApply = ({ jobPostingId, positionId }: { jobPostingId: string; positionId: string }) => {
    setApplyModal({
      open: true,
      jobPostingId,
      positionId,
    })
  }
  const onViewApplication = ({ jobPostingId, positionId }: { jobPostingId: string; positionId: string }) => {
    setApplicationModal({
      open: true,
      jobPostingId,
      positionId,
    })
  }

  const onCloseApplyModal = () => {
    setApplyModal({
      open: false,
      jobPostingId: null,
      positionId: null,
    })
  }
  const onCloseApplicationModal = () => {
    setApplicationModal({
      open: false,
      jobPostingId: null,
      positionId: null,
    })
  }

  const openSubscriptionCheckoutModal = () => {
    setOpenCheckoutModal(true)
  }

  useEffect(() => {
    if (sharedJobPosting?.id) {
      ampli.jobDetailView({
        job_id: decodeGraphqlNodeId(sharedJobPosting.id).numericalId || "",
        entry_point: "job-page",
        redacted: Boolean(creativeViewerData?.jobPosting.viewerRedactionReason),
      })
    }
  }, [creativeViewerData?.jobPosting.viewerRedactionReason, sharedJobPosting?.id])

  return (
    <>
      <ApplyToJobDrawer
        serverSession={serverSession}
        open={applyModal.open}
        jobPostingId={applyModal.jobPostingId}
        positionId={applyModal.positionId}
        onClose={onCloseApplyModal}
      />
      {/* Need to always render subscription modal for success screen */}
      <SubscriptionCheckoutModal
        entryPoint="job-detail-page"
        open={openCheckoutModal}
        onOpenChange={setOpenCheckoutModal}
      />

      <JobApplicationDrawer
        serverSession={serverSession}
        open={applicationModal.open}
        jobPostingId={applicationModal.jobPostingId}
        positionId={applicationModal.positionId}
        onClose={onCloseApplicationModal}
      />

      <div className="relative flex h-[calc(100dvh-72px)] w-full flex-col overflow-hidden md:h-dvh">
        <div className="mx-auto w-full flex-grow overflow-y-scroll pb-5 md:mt-[72px] md:max-w-[720px] md:px-12">
          <AuthenticatedJobDetailsContent
            openSubscriptionCheckoutModal={openSubscriptionCheckoutModal}
            serverSession={serverSession}
            jobPosting={sharedJobPosting}
            version="page"
          />
        </div>
        <JobsSideDrawerFooterTemplate>
          <AuthenticatedJobDetailsFooter
            clientViewerJobPosting={clientViewerJobPosting}
            creativeViewerJobPosting={creativeViewerJobPosting}
            serverSession={serverSession}
            loading={false}
            onApply={onApply}
            openSubscriptionCheckoutModal={openSubscriptionCheckoutModal}
            onViewApplication={onViewApplication}
          />
        </JobsSideDrawerFooterTemplate>
      </div>
    </>
  )
}
